import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import "./QuoteViewer.css";
import { QuoteViewerProps } from "./types";

import { FaQuoteRight } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { MdFileDownload } from "react-icons/md";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "~/components/ui/dropdown-menu";

import { useNavigate } from "react-router";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { VscMute, VscUnmute } from "react-icons/vsc";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";

const ANIMATION_DURATION = 0.2;

const QuoteViewer: React.FC<QuoteViewerProps> = ({ images, quotes, fetchMoreData, authorData }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const touchStartY = useRef<number | null>(null);
  const navigate = useNavigate();
  const { normalInter, medium } = useFontStyle();

  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [currentQuoteIdx, setCurrentQuoteIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"up" | "down">("up");
  const [allLoaded, setAllLoaded] = useState(false);
  const [isMute, setIsMute] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const lastScrollTime = useRef<number>(0);

  const lockScroll = () => document.body.classList.add("overflow-hidden");
  const unlockScroll = () => document.body.classList.remove("overflow-hidden");

  const getNextIndexes = (direction: "up" | "down") => {
    let imgIdx = currentImageIdx;
    let quoteIdx = currentQuoteIdx;

    if (direction === "down") {
      imgIdx = (imgIdx + 1) % images.length;
      quoteIdx = (quoteIdx + 1) % quotes.length;
    } else {
      imgIdx = (imgIdx - 1 + images.length) % images.length;
      quoteIdx = (quoteIdx - 1 + quotes.length) % quotes.length;
    }

    return { imgIdx, quoteIdx };
  };

  const triggerSlide = async (direction: "up" | "down") => {
    if (isAnimating) return;

    if (!allLoaded && direction === "down" && currentImageIdx === images?.length - 1) {
      const result = await fetchMoreData();
      if (result === null) setAllLoaded(true);
    }

    const { imgIdx, quoteIdx } = getNextIndexes(direction);
    setSlideDirection(direction);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentImageIdx(imgIdx);
      setCurrentQuoteIdx(quoteIdx);
      setIsAnimating(false);
      playAudio(quotes[quoteIdx].audio || "");
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        }
      }
    }, ANIMATION_DURATION * 1000);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (isAnimating) return;
    const now = Date.now();
    if (now - lastScrollTime.current < 600) return;
    lastScrollTime.current = now;
    triggerSlide(e.deltaY > 0 ? "down" : "up");
  };

  const handleNavigation = (direction: "up" | "down") => {
    triggerSlide(direction);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (isAnimating) return;
    touchStartY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (isAnimating || touchStartY.current === null) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(deltaY) > 50) {
      const direction = deltaY > 0 ? "up" : "down";
      triggerSlide(direction);
      touchStartY.current = null;
    }
  };

  const playAudio = (audioUrl: string) => {
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };

  const muteAudioPlaying = async () => {
    if (audioRef.current) {
      const _isMute = !audioRef.current.paused && audioRef.current.currentTime > 0;
      if (_isMute) {
        setIsMute(false);
        await audioRef.current.pause();
        audioRef.current.muted = true;
      } else {
        setIsMute(true);
        await audioRef.current.play();
        audioRef.current.muted = false;
      }
    }
  };

  const formatText = (text: string) => {
    const parts = text.split(/[,;]/g);
    const separators = text.match(/[,;]/g) || [];
    const elements = parts.map((part, idx) => {
      const color = "#ffff";
      return (
        <span key={idx} style={{ color }}>
          {part.trim()}
          {separators[idx] ? separators[idx] + "\n" : ""}
        </span>
      );
    });
    return elements;
  };

  const downloadAudio = async () => {
    const audioUrl = quotes[currentQuoteIdx]?.audio;
    if (!audioUrl) return;
    setIsDownloading(true);
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quote-audio-${currentQuoteIdx + 1}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMenuOpen(false);
    } catch (err) {
      console.error("Error downloading audio:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const getAuthorName = (authorId: number): string | null => {
    const author = authorData?.find((author) => author.id === authorId);
    return author ? author.text : null;
  };

  useEffect(() => {
    (async () => {
      try {
        if (quotes[currentQuoteIdx]?.audio) {
          playAudio(quotes[currentQuoteIdx].audio || "");
        }
        if (audioRef.current) {
          muteAudioPlaying();
        }
      } catch (err) {
        console.error("Error playing audio:", err);
      }
    })();

    lockScroll();
    return () => {
      unlockScroll();
    };
  }, []);

  if (!images?.length || !quotes?.length) return null;

  return (
    <div className="overflow-hidden w-full h-full" onTouchStart={onTouchStart} onTouchMove={onTouchMove}>
      {/* Background */}
      <div className="fixed w-full inset-0 lg:top-10 flex justify-center items-center">
        <div className="relative h-full lg:h-[80vh] aspect-[9/16] bg-black lg:rounded-2xl shadow-lg overflow-hidden" onWheel={handleWheel}>
          <div className="w-full flex justify-between md:justify-end items-center absolute top-0 right-0 z-50">
            <button
              className="md:hidden bg-gray-100/35 w-[50px] h-[50px] flex items-center justify-center  text-white rounded-full p-2 m-2 hover:bg-gray-100/40 transition duration-300"
              onClick={() => navigate(-1)}
            >
              <MdOutlineArrowBackIos className="w-6 h-6" />
            </button>

            <div className="flex ">
              <button
                className="bg-gray-100/35 w-[50px] h-[50px] flex items-center justify-center  text-white rounded-full p-2 m-2 hover:bg-gray-100/40 transition duration-300"
                onClick={muteAudioPlaying}
              >
                {isMute ? <VscUnmute className="w-6 h-6" /> : <VscMute className="w-6 h-6" />}
              </button>

              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="bg-gray-100/35 w-[50px] h-[50px] flex items-center justify-center text-white rounded-full p-2 m-2 hover:bg-gray-100/40 transition duration-300"
                    aria-label="More options"
                  >
                    <BsThreeDotsVertical className="w-6 h-6" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[100]">
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={downloadAudio} disabled={isDownloading} className="gap-2">
                    {isDownloading ? (
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    ) : (
                      <MdFileDownload className="w-5 h-5" />
                    )}
                    {isDownloading ? "Downloading..." : "Download Audio"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <audio className="hidden z-50" src={quotes[currentImageIdx]?.audio || ""} autoPlay controls ref={audioRef} preload="auto">
              <track className="" kind="captions" src="/captions/audio-captions.vtt" srcLang="en" label="English" />
            </audio>
          </div>

          <AnimatePresence initial={false} custom={slideDirection}>
            <motion.div
              key={currentImageIdx}
              className="absolute inset-0 w-full bg-black h-full"
              style={{
                backgroundImage: `url(${images[currentImageIdx]})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                // backgroundPosition: "center",
                willChange: "transform",
                aspectRatio: "9 / 16",
              }}
              initial={{ y: slideDirection === "down" ? "100%" : "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: slideDirection === "down" ? "-100%" : "100%" }}
              transition={{
                y: { duration: ANIMATION_DURATION, ease: "easeInOut" },
              }}
            >
              <link rel="preload" as="image" href={images[(currentImageIdx + 1) % images.length]} />
              <img
                src={images[(currentImageIdx + 1) % images.length]}
                alt="Preloaded background image"
                style={{ display: "none", userSelect: "none" }}
                aria-hidden="true"
              />

              <div className="absolute bottom-0 w-full p-6 bg-gradient-to-t from-black to-transparent h-[55vh] z-10 pointer-events-none"></div>

              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={currentQuoteIdx}
                  initial={{ opacity: 0, y: slideDirection === "down" ? 60 : -60, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: slideDirection === "down" ? -60 : 60, scale: 0.97 }}
                  transition={{ duration: 0.45, ease: "easeInOut" }}
                  className="absolute bottom-0 w-full z-50 flex flex-col items-center justify-center p-6"
                >
                  {quotes && (
                    <>
                      <div className="text-2xl text-center font-bold mb-2 text-white">
                        {quotes[currentQuoteIdx]?.ref ? (
                          <span className={cn("capitalize", medium)}>{quotes[currentQuoteIdx]?.ref}</span>
                        ) : (
                          <FaQuoteRight className="w-6 h-6 inline-block mr-2 text-white" />
                        )}
                      </div>
                      <p
                        className={cn(
                          "body_text_l mb-2 text-center whitespace-pre-line font-bold text-white font-baloobhai2Regular tracking-wide",
                          normalInter,
                        )}
                      >
                        {formatText(quotes[currentQuoteIdx]?.quote)}
                      </p>
                      {quotes[currentQuoteIdx]?.author && getAuthorName(quotes[currentQuoteIdx]?.author) ? (
                        <p className={cn("text-md italic body_text_s text-white", normalInter)}>- {getAuthorName(quotes[currentQuoteIdx]?.author)}</p>
                      ) : null}
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="hidden lg:flex flex-col gap-4 ml-5">
          <button className="nav-button up" onClick={() => handleNavigation("up")} disabled={isAnimating}>
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
            </svg>
          </button>
          <button className="nav-button down" onClick={() => handleNavigation("down")} disabled={isAnimating}>
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteViewer;
